import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Space } from 'antd';
import {
  getServiceStatus, isRunning, removeService, restartService, serviceState, sessionIdFor, stopService,
} from '../../services/SandboxService';
import { markHomeStep } from '../../services/SettingsService';
import { startServices } from './startProgress';
import StartButton from './StartButton';
import { PlanChangeButton } from './PlanRequest';
import { clearStart, runStart, serviceStartKey, stopStart, useStart } from './startRuns';
import useRepoFlags from '../../hooks/useRepoFlags';
import { NO_BUILD_TIP } from '../repoFlags/RepoFlagBadges';

/**
 * Start / Stop / Restart / Logs for one service of a project, the same everywhere a service is listed (sandbox
 * home, project dashboard). `service` is the sandbox overview's entry for it, or null when it has no session yet
 * (never built, or reaped while idle): then Start builds it from vfs-service. `showRemove` false: the caller offers
 * Remove itself (the project Home, in a row's ⋯). A service flagged No build (context only) is never started: no Start
 * or Restart, "Context only" in their place (Stop and Logs stay, for a preview still running). `onError(message)`: the
 * caller shows why a start or stop failed in its own place (the Apps page's problem box; null once it is gone) instead
 * of a red line next to Logs. `runnable` (`{ service, kind, file }`, utils/runnablesModel): a Dockerfile or compose file
 * pinned in a service, `serviceName` its runner name, started as a session of its own.
 */
export default function ServiceActions({
  projectId, serviceName, service = null, onChanged, onOpenLogs, size = 'small', showRestart = true, showRemove = true, pollMs = undefined,
  restartRequest = 0, startRequest = 0, before = null, beforeLogs = null, onError = null, runnable = null, wrap = true,
}) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  // The plan's running-services limit refused the start: "Ask for another plan" next to the message.
  const [limitHit, setLimitHit] = useState(false);
  const [progress, setProgress] = useState(null); // { phase, percent } while a restart builds
  const watching = useRef(0);
  const sessionId = service?.sessionId || sessionIdFor(projectId, serviceName);
  const running = isRunning(service);
  const contextOnly = Boolean(useRepoFlags(projectId).serviceFlags(serviceName)?.noBuild);
  // A failed build or a crash leaves an entry behind: it can be removed from the runner (Start builds it again).
  const broken = Boolean(service) && ['failed', 'crashed'].includes(serviceState(service));

  // The caller's own place for the message, when it has one.
  useEffect(() => { onError?.(error); }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; watching.current += 1; };
  }, []);
  const startKey = serviceStartKey(projectId, serviceName);
  const { progress: startProgress, starting: startGoing } = useStart(startKey);
  // A start's progress while it goes on (once it runs, the usual buttons again).
  const starting = startGoing ? startProgress : null;

  /** Until the app answers (a port) or fails: the build step on the button, then a refresh of the list. */
  const watchStart = async() => {
    const id = ++watching.current;
    const started = Date.now();
    setProgress({ phase: 'Starting', percent: null });
    while (id === watching.current && Date.now() - started < 7 * 60 * 1000) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, 2000); });
      let info = null;
      try {
        // eslint-disable-next-line no-await-in-loop
        info = await getServiceStatus(sessionId);
      } catch { /* a blip: ask again */ }
      if (id !== watching.current) { return; }
      if (info?.previewPort || info?.containerStatus === 'running') { break; }
      // Stopped meanwhile (here, or from another tab): the build was cancelled, not failed.
      if (info?.buildPhase === 'cancelled') { break; }
      if (info?.crashed || info?.buildStatus === 'failed' || info?.buildStatus === 'error') {
        setError(`${serviceName} could not start: ${info.buildError || info.processError || 'see Logs'}`);
        break;
      }
      const phase = info?.buildPhase ? info.buildPhase.charAt(0).toUpperCase() + info.buildPhase.slice(1) : 'Starting';
      setProgress({ phase, percent: typeof info?.buildProgress === 'number' ? Math.round(info.buildProgress) : null });
      onChanged?.();
    }
    if (id === watching.current) { setProgress(null); onChanged?.(); }
  };

  /**
   * Start: asks the runner, then follows the service until it runs ("Installing 55%…" on the button). It goes on when
   * this unmounts (startRuns.js); a failure says why.
   */
  const start = () => {
    setError(null);
    runStart(startKey, (onProgress, shouldStop) => startServices(projectId, [{ serviceName, service, runnable }], {
      pollMs,
      shouldStop,
      onProgress: (next) => { onProgress(next); onChanged?.(); },
    })).then((last) => {
      const entry = last?.services[0];
      if (entry && (entry.phase === 'failed' || entry.phase === 'limit')) {
        clearStart(startKey);
        if (mounted.current) { setError(entry.phase === 'limit' ? entry.reason : `${serviceName} could not start: ${entry.reason}`); }
      }
      markHomeStep('previewOpened');
    }).finally(() => onChanged?.());
  };

  const run = async(action, fn) => {
    setBusy(action);
    setError(null);
    setLimitHit(false);
    try {
      await fn();
      const starts = action !== 'stop' && action !== 'remove';
      if (starts) { markHomeStep('previewOpened'); }
      onChanged?.();
      if (starts) { watchStart(); }
    } catch (err) {
      setLimitHit(err?.code === 'limit_running_services');
      setError(err?.code === 'limit_running_services'
        ? err.message
        : `Could not ${action} ${serviceName}: ${err?.message || 'unknown error'}`);
    } finally {
      setBusy(null);
    }
  };

  // Stop while it starts or builds: ends the build in the runner (its install is killed, its place in the queue freed).
  const cancelStart = () => {
    watching.current += 1;
    setProgress(null);
    stopStart(startKey);
    return run('stop', () => stopService(sessionId));
  };

  // A Restart asked from outside (the project Home's row "⋯"): each new `restartRequest` restarts the service the same
  // way as the button, with its progress on the button.
  const lastRestartRequest = useRef(restartRequest);
  useEffect(() => {
    if (restartRequest === lastRestartRequest.current) { return; }
    lastRestartRequest.current = restartRequest;
    if (running && !busy) { run('restart', () => restartService(sessionId)); }
  }, [restartRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // A Start asked from outside (the Apps page's "Save and start", after How it runs changed): a running service restarts,
  // one that is not running (a failed build too) starts, the same way as the buttons.
  const lastStartRequest = useRef(startRequest);
  useEffect(() => {
    if (startRequest === lastStartRequest.current) { return; }
    lastStartRequest.current = startRequest;
    if (busy || startGoing || contextOnly) { return; }
    if (running) { run('restart', () => restartService(sessionId)); } else { start(); }
  }, [startRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Space className={wrap ? undefined : 'sbx-actions-line'} size={6} wrap={wrap}>
      {before}
      {progress ? (
        <Button disabled loading size={size}>
          {progress.phase}{progress.percent !== null ? ` ${progress.percent}%` : ''}…
        </Button>
      ) : starting ? (
        <StartButton label="Start" onStart={() => {}} progress={starting} size={size} />
      ) : running ? (
        <Button loading={busy === 'stop'} onClick={() => run('stop', () => stopService(sessionId))} size={size}>
          Stop
        </Button>
      ) : contextOnly ? (
        <span className="repo-flag-context-only" data-testid="service-context-only" title={NO_BUILD_TIP}>Context only</span>
      ) : (
        <StartButton disabled={startGoing || Boolean(busy)} label="Start" onStart={start} size={size} />
      )}
      {showRestart && running && !contextOnly && (
        <Button loading={busy === 'restart'} onClick={() => run('restart', () => restartService(sessionId))} size={size}>
          Restart
        </Button>
      )}
      {showRemove && broken && !progress && !starting && (
        <Button
          aria-label={`Remove ${serviceName}`}
          danger
          loading={busy === 'remove'}
          onClick={() => run('remove', () => removeService(sessionId))}
          size={size}
        >
          Remove
        </Button>
      )}
      {beforeLogs}
      {onOpenLogs && (
        <Button onClick={() => onOpenLogs(serviceName)} size={size}>Logs</Button>
      )}
      {(progress || starting) && (
        <Button loading={busy === 'stop'} onClick={cancelStart} size={size} title="Stop it and cancel the build">
          Stop
        </Button>
      )}
      {error && !onError && <span className="sbx-action-error" role="alert">{error}</span>}
      {(limitHit || startProgress?.services?.some((entry) => entry.phase === 'limit')) && <PlanChangeButton />}
    </Space>
  );
}

ServiceActions.propTypes = {
  projectId: PropTypes.string.isRequired,
  serviceName: PropTypes.string.isRequired,
  service: PropTypes.object,
  onChanged: PropTypes.func,
  onOpenLogs: PropTypes.func,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  showRemove: PropTypes.bool,
  showRestart: PropTypes.bool,
  /** How often a start looks at the service (tests). */
  pollMs: PropTypes.number,
  /** Incremented by the caller to restart the service (a menu item elsewhere), as the Restart button does. */
  restartRequest: PropTypes.number,
  /** Each new value starts the service (restarts it when it runs): "Save and start" on the Apps page. */
  startRequest: PropTypes.number,
  /** Shown before the other buttons, in the same wrapping row (Home's Running now puts Open there). */
  before: PropTypes.node,
  /** Shown right before Logs, in the same row (the Apps page's Code). */
  beforeLogs: PropTypes.node,
  /** (message | null): shows the error in the caller's place instead of next to the buttons. */
  onError: PropTypes.func,
  /** A Dockerfile or compose file pinned in a service (utils/runnablesModel), started as its own session. */
  runnable: PropTypes.shape({ service: PropTypes.string, kind: PropTypes.string, file: PropTypes.string }),
  /** False: the buttons stay on one line (a compact table row, the project Home's Apps). */
  wrap: PropTypes.bool,
};
